import { supabase, Sentry, SupabaseError, withRetry, shouldRethrow } from './shared'
import type { UserSoundSubmission } from '../supabase-types'
import { assertRiffWave } from '../wav-container'

const USER_SUBMISSIONS_BUCKET = 'user-submissions'

function resolveSubmissionStorageLocation(filePath: string): {
  bucket: string
  path: string
} {
  if (filePath.startsWith('submissions/')) {
    return {
      bucket: 'audio-files',
      path: filePath,
    }
  }

  return {
    bucket: USER_SUBMISSIONS_BUCKET,
    path: filePath,
  }
}

// User sound submissions API (requires authentication)
export const submissionsApi = {
  // Submit a new sound for review
  async submitSound(data: {
    user_id: string
    title: string
    description?: string
    category_id?: string
    file_path: string
    file_size: number
    duration_seconds: number
    ip_address?: string
    user_agent?: string
  }): Promise<UserSoundSubmission | null> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data: submission, error } = await supabase
          .from('user_sound_submissions')
          .insert([{
            user_id: data.user_id,
            title: data.title,
            description: data.description,
            category_id: data.category_id,
            file_path: data.file_path,
            file_size: data.file_size,
            duration_seconds: data.duration_seconds,
            status: 'pending',
            ip_address: data.ip_address,
            user_agent: data.user_agent
          }])
          .select()
          .single()

        if (error) {
          throw new SupabaseError(`Failed to submit sound: ${error.message}`, error)
        }

        return submission as UserSoundSubmission
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'submissionsApi', action: 'submitSound' } })
      return null
    }
  },

  // Get user's own submissions
  async getUserSubmissions(userId: string, options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<UserSoundSubmission[]> {
    if (!supabase) {
      return []
    }
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase
          .from('user_sound_submissions')
          .select('*')
          .eq('user_id', userId)
          .order('submitted_at', { ascending: false })

        if (error) {
          throw new SupabaseError(`Failed to fetch submissions: ${error.message}`, error)
        }

        return (data as UserSoundSubmission[]) || []
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'submissionsApi', action: 'getUserSubmissions' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  // Upload sound file to storage
  async uploadSoundFile(userId: string, file: File): Promise<string | null> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'wav'
      const fileName = `${crypto.randomUUID()}.${fileExt}`
      const filePath = `${userId}/${fileName}`

      // Container gate (TES-2236). The upload is stamped `audio/wav` from the
      // extension alone; an approved submission is later served as
      // LockChime.wav, so accepting non-RIFF bytes here reintroduces the
      // TES-2154 defect one review away from the catalog.
      const bytes = new Uint8Array(await file.arrayBuffer())
      assertRiffWave(bytes, filePath)

      const { error } = await supabase.storage
        .from(USER_SUBMISSIONS_BUCKET)
        .upload(filePath, bytes, {
          contentType: 'audio/wav',
          upsert: false
        })

      if (error) {
        throw new SupabaseError(`Failed to upload file: ${error.message}`, error)
      }

      return filePath
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'submissionsApi', action: 'uploadSoundFile' } })
      return null
    }
  },

  // Get user's upload count for today (for rate limiting)
  async getUploadCount(userId: string): Promise<number> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const { count, error } = await supabase
        .from('user_sound_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('submitted_at', today.toISOString())

      if (error) {
        throw new SupabaseError(`Failed to get upload count: ${error.message}`, error)
      }

      return count || 0
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'submissionsApi', action: 'getUploadCount' } })
      return 0
    }
  },

  // Delete a submission (only allowed for pending submissions by owner)
  async deleteSubmission(submissionId: string, userId: string): Promise<boolean> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // First get the submission to check ownership and get file path
      const { data: submission, error: fetchError } = await supabase
        .from('user_sound_submissions')
        .select('*')
        .eq('id', submissionId)
        .eq('user_id', userId)
        .eq('status', 'pending')
        .single()

      if (fetchError || !submission) {
        return false
      }

      // Delete the file from storage
      if (submission.file_path) {
        const storageLocation = resolveSubmissionStorageLocation(submission.file_path)
        await supabase.storage
          .from(storageLocation.bucket)
          .remove([storageLocation.path])
      }

      // Delete the submission record
      const { error: deleteError } = await supabase
        .from('user_sound_submissions')
        .delete()
        .eq('id', submissionId)
        .eq('user_id', userId)

      if (deleteError) {
        throw new SupabaseError(`Failed to delete submission: ${deleteError.message}`, deleteError)
      }

      return true
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'submissionsApi', action: 'deleteSubmission' } })
      return false
    }
  }
}
